import * as React from "react";
import { Button, Card, Divider, Modal, notification, Select, Tag } from "antd";
import { getLocalToken, linksOf, request } from "../../services/cpln";
import { KUBERNETES_READINESS_UPDATE_INTERVAL_MS, MK8S_DASHBOARD_TOKEN_UPDATE_INTERVAL_MS } from "../../envVariables";
import { useKubernetesReadinessReducer } from "../../components/detail/kubernetesReadinessReducer";
import { Mk8sCluster } from "../../schema/types/mk8s/entity/mk8s";
import { useGetMk8sClusterQuery } from "../../store/api/mk8sApi";
import { tagLinkUrlPrefixes } from "../../services/utils";
import { CardContent } from "../../components/antd/card/CardContent";
import { FormItem } from "../../components/antd/form/FormItem";
import { FormLabel } from "../../components/antd/form/FormLabel";
import { CaretRightOutlined, LinkOutlined } from "@ant-design/icons";
import { FormItemValue } from "../../components/antd/form/FormItemValue";
import { TagLinksTable } from "../../components/detail/tagLinksTable";
import { SyntaxHighlight } from "../../components/syntaxHighlight/SyntaxHighlight";
import jsYaml from "js-yaml";
import { CpFormItem } from "../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../gvc/testNewForm/CpLabel";
import { CpLabelDirtyIndicator } from "../gvc/testNewForm/CpLabelDirtyIndicator";
import { CpTextArea } from "../gvc/testNewForm/CpTextArea";
import { CpErrorWarning } from "../gvc/testNewForm/CpErrorWarning";
import { DetectedTags } from "../../components/detail/detectedTags";

interface Props {
  mk8s: Mk8sCluster;
}
export const Summary: React.FC<Props> = ({ mk8s }) => {
  const selfLink = linksOf(mk8s).self!;

  const { refetch } = useGetMk8sClusterQuery(selfLink, {
    skip: !selfLink,
  });

  const [isLoading, setIsLoading] = React.useState(false);
  const [specVersion, setSpecVersion] = React.useState(mk8s.spec.version);
  const [isUpgradingVersion, setIsUpgradingVersion] = React.useState(false);
  const [isAddonStatusExpanded, setIsAddonStatusExpanded] = React.useState(false);

  const hasTagLinks =
    Object.entries(mk8s.tags || {}).filter(([_, value]) => tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)))
      .length > 0;

  const { state, fetchReadiness } = useKubernetesReadinessReducer();

  const [versionOptions, setVersionOptions] = React.useState<any[]>([]);
  const [fetchedVersionOptions, setFetchedVersionOptions] = React.useState<boolean>(false);
  const isLatest = React.useMemo(() => {
    if (versionOptions.length < 1) {
      return false;
    }

    return versionOptions[0].value === mk8s.spec.version;
  }, [mk8s.spec.version, versionOptions]);
  const versionIndex = React.useMemo(() => {
    if (!fetchedVersionOptions) {
      return -1;
    }
    const index = versionOptions.findIndex((i) => i.value === mk8s.spec.version);
    return index;
  }, [mk8s.spec.version, versionOptions, fetchedVersionOptions]);

  React.useEffect(() => {
    fetchSchemaData();

    const unsubscribe = fetchReadiness([mk8s.name], KUBERNETES_READINESS_UPDATE_INTERVAL_MS);
    return () => {
      unsubscribe();
    };
  }, []);

  async function fetchSchemaData() {
    // Versions
    try {
      const { data: schemaRes } = await request({ url: "/catalog/schema/mk8s" });
      const versions: string[] = schemaRes.keys.spec.keys.version.allow;
      const _versionOptions = versions.map((v) => ({ label: v, value: v })).reverse();
      setVersionOptions(_versionOptions);
      setFetchedVersionOptions(true);
    } catch (e) {
      console.error("Failed to get versions from mk8s catalog schema");
    }
  }

  const dashboardFormRef = React.useRef<HTMLFormElement>(null as any);
  const headlampFormRef = React.useRef<HTMLFormElement>(null as any);
  const [tokenFromLocal, setTokenFromLocal] = React.useState("");
  React.useEffect(() => {
    getLocalToken().then((res) => {
      if (res.accessToken !== "pass") {
        setTokenFromLocal(res.accessToken);
      }
    });
    const localTokenInterval = setInterval(() => {
      getLocalToken().then((res) => {
        if (res.accessToken !== "pass") {
          setTokenFromLocal(res.accessToken);
        }
      });
    }, MK8S_DASHBOARD_TOKEN_UPDATE_INTERVAL_MS);
    return () => {
      clearInterval(localTokenInterval);
    };
  }, []);

  function handleDashboard(e: any) {
    e.preventDefault();
    if (dashboardFormRef.current) {
      dashboardFormRef.current.submit();
    }
  }

  function handleHeadlamp(e: any) {
    e.preventDefault();
    if (headlampFormRef.current) {
      headlampFormRef.current.submit();
    }
  }

  const readiness = state.readinessMap[mk8s.name] || { ready: false };
  const fetchedReadiness = state.requestedClusterNames.includes(mk8s.name);

  const { dashboard, headlamp, logs, metrics, ...processedAddonStatus } = mk8s.status?.addOns || {};

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardContent>
            <CpFormItem>
              <CpLabel name={`description`}>
                <FormLabel>Description</FormLabel>
                <CpLabelDirtyIndicator name={`description`} />
              </CpLabel>
              <CpTextArea autoSize name={`description`} style={{ maxWidth: 600 }} />
              <CpErrorWarning name={`description`} />
            </CpFormItem>
            <FormItem>
              <FormLabel>Alias</FormLabel>
              <FormItemValue>{mk8s.alias}</FormItemValue>
            </FormItem>
            <FormItem>
              <div className={`flex items-center gap-2`}>
                <FormLabel>Kubernetes Version</FormLabel>
                {fetchedVersionOptions ? (
                  isLatest ? (
                    <Tag>Latest</Tag>
                  ) : (
                    <Button size={`small`} onClick={() => setIsUpgradingVersion(true)}>
                      Upgrade
                    </Button>
                  )
                ) : null}
              </div>
              <FormItemValue>{mk8s.spec.version}</FormItemValue>
            </FormItem>
            <FormItem>
              <FormLabel>Status</FormLabel>
              <FormItemValue>
                <span className={`${fetchedReadiness ? (readiness.ready ? "color-action" : "color-danger") : ""}`}>
                  {fetchedReadiness ? (readiness.ready ? "Ready" : "Not Ready") : "Loading"}
                </span>
                {fetchedReadiness && readiness.message ? <span> | {readiness.message}</span> : null}
              </FormItemValue>
            </FormItem>
            {mk8s.status?.addOns?.dashboard?.url ? (
              <FormItem>
                <FormLabel>Dashboard URL</FormLabel>
                <FormItemValue>
                  <div className="flex flex-col items-start gap-1">
                    <button type={"button"} className="color-link flex items-center gap-1" onClick={handleDashboard}>
                      <span>{mk8s.status?.addOns?.dashboard?.url}</span>
                      <LinkOutlined />
                    </button>
                    <form
                      className="hidden"
                      ref={dashboardFormRef}
                      action={`${mk8s.status?.addOns?.dashboard?.url}/auth/process/#/node?namespace=default`}
                      method="POST"
                      target="_blank"
                    >
                      <input name="redirect" value={mk8s.status?.addOns?.dashboard?.url} type="hidden" />
                      <input name="idToken" value={tokenFromLocal} type="hidden" />
                      <button type="submit">dashboard</button>
                    </form>
                  </div>
                </FormItemValue>
              </FormItem>
            ) : null}
            {mk8s.status?.addOns?.headlamp?.url ? (
              <FormItem>
                <FormLabel>Headlamp URL</FormLabel>
                <FormItemValue>
                  <div className="flex flex-col items-start gap-1">
                    <button type={"button"} className="color-link flex items-center gap-1" onClick={handleHeadlamp}>
                      <span>{mk8s.status?.addOns?.headlamp?.url}</span>
                      <LinkOutlined />
                    </button>
                    <form
                      className="hidden"
                      ref={headlampFormRef}
                      action={`${mk8s.status?.addOns?.headlamp?.url}`}
                      method="POST"
                      target="_blank"
                    >
                      <input
                        name="redirect"
                        value={`${mk8s.status?.addOns.headlamp.url}/c/${mk8s.name}/namespaces`}
                        type="hidden"
                      />
                      <input name="idToken" value={tokenFromLocal} type="hidden" />
                      <button type="submit">headlamp</button>
                    </form>
                  </div>
                </FormItemValue>
              </FormItem>
            ) : null}
            <DetectedTags
              value={mk8s.tags}
              detectValue={(tags, key) => {
                const hasTag = tags.hasOwnProperty(key);
                if (!hasTag) {
                  return false;
                }
                if (key === "cpln/ignore") {
                  const value = tags[key];
                  return String(value) === "true";
                }
                return true;
              }}
              detectionMap={{
                "cpln/ignore": "Stops processing changes to this object",
              }}
              pluralLabel="Tags"
            />
            {hasTagLinks ? (
              <TagLinksTable
                tableId="mk8s-info-tag-links"
                tags={Object.entries(mk8s.tags || {})
                  .filter(([_, value]) => tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)))
                  .map(([key, value]) => ({ key, value }))}
                styles={{ container: { width: "100%" }, header: { marginBottom: 0 } }}
              />
            ) : null}
            {Object.keys(processedAddonStatus).length > 0 ? (
              <div className="mt-8">
                <Divider
                  orientation="left"
                  orientationMargin={0}
                  style={isAddonStatusExpanded ? {} : { marginBottom: 0 }}
                >
                  <div
                    className={`flex items-baseline gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      setIsAddonStatusExpanded((x) => !x);
                    }}
                  >
                    <CaretRightOutlined rotate={isAddonStatusExpanded ? 90 : 0} className={`self-center`} />
                    <span>Addon Statuses</span>
                  </div>
                </Divider>
                {isAddonStatusExpanded ? (
                  <>
                    {mk8s.status?.addOns?.awsECR ? (
                      <div className="mb-6">
                        <FormLabel>AWS ECR</FormLabel>
                        <SyntaxHighlight
                          code={jsYaml.dump(mk8s.status?.addOns?.awsECR)}
                          language="yaml"
                        />
                      </div>
                    ) : null}
                    {mk8s.status?.addOns?.awsEFS ? (
                      <div className="mb-6">
                        <FormLabel>AWS EFS</FormLabel>
                        <SyntaxHighlight
                          code={jsYaml.dump(mk8s.status?.addOns?.awsEFS)}
                          language="yaml"
                        />
                      </div>
                    ) : null}
                    {mk8s.status?.addOns?.awsELB ? (
                      <div className="mb-6">
                        <FormLabel>AWS ELB</FormLabel>
                        <SyntaxHighlight
                          code={jsYaml.dump(mk8s.status?.addOns?.awsELB)}
                          language="yaml"
                        />
                      </div>
                    ) : null}
                    {mk8s.status?.addOns?.awsWorkloadIdentity ? (
                      <div className="mb-6">
                        <FormLabel>AWS Workload Identity</FormLabel>
                        <SyntaxHighlight
                          code={jsYaml.dump(mk8s.status?.addOns?.awsWorkloadIdentity)}
                          language="yaml"
                        />
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}
            <Modal
              open={isUpgradingVersion}
              title="Upgrade Kubernetes Version"
              okText="Save"
              cancelText="Cancel"
              cancelButtonProps={{ disabled: isLoading }}
              okButtonProps={{ disabled: isLoading, loading: isLoading }}
              onCancel={() => {
                setSpecVersion(mk8s.spec.version);
                setIsUpgradingVersion(false);
              }}
              destroyOnHidden
              closable={false}
              maskClosable={false}
              onOk={async () => {
                setIsLoading(true);
                try {
                  if (specVersion === mk8s.spec.version) {
                    setIsUpgradingVersion(false);
                    return;
                  }
                  const body = { spec: { version: specVersion } };
                  await request({ url: selfLink, method: "patch", body: body });
                  notification.success({ message: "Success", description: "Kubernetes version is upgrade" });
                  setIsUpgradingVersion(false);
                  refetch();

                  setIsLoading(false);
                } catch (e) {
                  setIsUpgradingVersion(false);
                  setIsLoading(false);
                }
              }}
            >
              <CpFormItem>
                <CpLabel name={`specVersion`}>Version to Upgrade</CpLabel>
                <Select
                  id={`specVersion`}
                  value={specVersion}
                  onChange={(value) => setSpecVersion(value)}
                  options={versionOptions.filter((i, idx) => idx <= versionIndex)}
                />
              </CpFormItem>
            </Modal>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
